import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import CommentItem from './CommentItem';
import CommentForm from './CommentForm';
import '../styles/comment-section.css';

const CommentSection = ({ contentId, initialCommentCount = 0, onCommentCountChange }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(initialCommentCount);
  const { user } = useAuth();
  const { t } = useLanguage();

  const fetchComments = async () => {
    if (!contentId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`/api/content?operation=getComments&contentId=${contentId}`);
      const data = await response.json();
      
      if (response.ok) {
        setComments(data.comments || []);
        setCommentCount(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showComments) {
      fetchComments();
    }
  }, [showComments, contentId]);

  const handleCommentAdded = (newComment) => {
    // Always refetch comments to get proper hierarchical structure and updated counts
    fetchComments();
    // Notify parent component to refresh news data
    if (onCommentCountChange) {
      onCommentCountChange();
    }
  };

  const handleCommentDeleted = (commentId) => {
    // Refetch comments to get updated hierarchy and counts
    fetchComments();
    // Notify parent component to refresh news data
    if (onCommentCountChange) {
      onCommentCountChange();
    }
  };

  const toggleComments = (e) => {
    e.stopPropagation(); // Prevent event bubbling to parent news item
    setShowComments(!showComments);
  };

  return (
    <div className="comment-section">
      <div className="comment-header">
        <button 
          className="comment-toggle"
          onClick={toggleComments}
        >
          💬 {commentCount} {commentCount === 1 ? t('news.comments.comment') : t('news.comments.comments')}
          <span className="toggle-icon">{showComments ? '▲' : '▼'}</span>
        </button>
      </div>

      {showComments && (
        <div className="comment-content" onClick={(e) => e.stopPropagation()}>
          <CommentForm
            contentId={contentId}
            onCommentAdded={handleCommentAdded}
          />

          {loading && (
            <div className="comment-loading">{t('news.loadingNews')}</div>
          )}

          <div className="comment-list">
            {comments.map(comment => (
              <CommentItem
                key={comment.id}
                comment={comment}
                currentUser={user}
                depth={comment.depth || 0}
                onDeleted={handleCommentDeleted}
                onReply={handleCommentAdded}
              />
            ))}
          </div>

          {comments.length === 0 && !loading && (
            <div className="no-comments">
              {t('news.comments.noComments')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentSection;